import { agenciesApi } from 'Api/Agencies/agenciesApi';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  ReportingDashboardState,
  goalsLoaded,
  goalsStartedLoading,
} from '../reportingDashboardReducer';
import { useContext, useEffect, useMemo } from 'react';
import { LoadingState } from '../../../utils/loadingState';
import { EntityGoalsModel, SaleGoals } from 'Api/Agencies/Types/Agencies';
import { AuthContext } from '@urbanx/agx.auth.client';
import { StatisticsForEntityRequestEntityType } from '@urbanx/agx.rex.client';

export const GoalsEntityType = { AgentOrTeam: 0, Agency: 1, Partnership: 2 };

const useGoals = (
  entityType: StatisticsForEntityRequestEntityType
): [
  SaleGoals | undefined,
  (entityType: StatisticsForEntityRequestEntityType, saleGoals: SaleGoals) => void,
] => {
  const reportingDashboardState: ReportingDashboardState = useAppSelector(
    state => state.reportingDashboard
  );

  const { goals, goalsLoadingState } = reportingDashboardState;

  const dispatch = useAppDispatch();

  const { userAccount, getAuthToken } = useContext(AuthContext);

  const loadGoals = async () => {
    if (!getAuthToken) return;

    dispatch(goalsStartedLoading());
    const token = await getAuthToken();

    if (!token) return;

    const goalsResponse =
      await agenciesApi(token).get<EntityGoalsModel>('GetEntityGoals');
    const goals = goalsResponse.data;

    dispatch(goalsLoaded({ ...goals }));
  };

  useEffect(() => {
    if (!userAccount) return;

    if (goalsLoadingState === LoadingState.NotLoaded) {
      loadGoals();
    }
  }, [goalsLoadingState, userAccount]);

  const saveGoals = async (entityType: StatisticsForEntityRequestEntityType, saleGoals: SaleGoals) => {
    if (!getAuthToken) return;

    const goalsEntityType =
      entityType === StatisticsForEntityRequestEntityType.IndividualOrTeam
        ? GoalsEntityType.AgentOrTeam
        : entityType === StatisticsForEntityRequestEntityType.Agency
          ? GoalsEntityType.Agency
          : GoalsEntityType.Partnership;

    const token = await getAuthToken();

    if (!token) return;

    await agenciesApi(token).post('SetEntityGoals', {
      entityType: goalsEntityType,
      saleGoals,
    });
    await loadGoals();
  };

  const relevantGoals = useMemo(() => {
    if (entityType === StatisticsForEntityRequestEntityType.IndividualOrTeam) {
      return goals?.agentOrTeamGoals;
    }

    if (
      entityType === StatisticsForEntityRequestEntityType.Agency ||
      entityType === StatisticsForEntityRequestEntityType.Partnership
    ) {
      return goals?.agencyGoals ?? undefined;
    }
  }, [entityType, goals]);

  return [relevantGoals, saveGoals];
};

export default useGoals;
